<template>
  <div id="app" class="min-h-screen bg-theme-bg-primary text-theme-text-primary transition-colors duration-200">
    
    <!-- Loading Screen -->
    <LoadingScreen v-if="isLoading" />
    
    <!-- Main App Content -->
    <div v-else class="flex flex-col min-h-screen">
      <!-- Navigation Header -->
      <SimpleHeaderNavigation v-if="showNavigation" />
      
      <!-- Main Content Area -->
      <main class="flex-1 relative">
        <!-- Page Transition -->
        <router-view v-slot="{ Component, route }">
          <component :is="Component" :key="route.path" />
        </router-view>
      </main>
      
      <!-- Bottom Navigation for Mobile -->
      <BottomNavigation v-if="showBottomNavigation" />
      
      <!-- Global Modals -->
      <GlobalModals />
    </div>
    
    <!-- Toast Notifications -->
    <Toast />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import { useHead } from '@vueuse/head'

// Components
import LoadingScreen from '@/components/ui/LoadingScreen.vue'
import SimpleHeaderNavigation from '@/components/navigation/SimpleHeaderNavigation.vue'
import BottomNavigation from '@/components/navigation/BottomNavigation.vue'
import GlobalModals from '@/components/modals/GlobalModals.vue'
import Toast from '@/components/ui/Toast.vue'

const route = useRoute()
const authStore = useAuthStore()
const appStore = useAppStore()

// Computed properties
const isLoading = computed(() => appStore.isLoading)
const showNavigation = computed(() => {
  const hideOnRoutes = ['login', 'register', 'forgot-password', 'reset-password']
  return !hideOnRoutes.includes(route.name as string)
})
const showBottomNavigation = computed(() => {
  return authStore.isAuthenticated && showNavigation.value
})

// Computed for canonical URL
const canonicalUrl = computed(() => `https://sugarm.me${route.path}`)

// Head management
useHead({
  title: 'SugarM｜不限性別的成人互助交友平台',
  meta: [
    {
      name: 'description',
      content: 'SugarM 是不限性別的成人互助交友平台。男生可找男生、女生可找女生；金主與 Baby 都能選擇男性、女性或兩者皆可。'
    },
    {
      name: 'keywords',
      content: '成人交友, 互助交友, Sugar Dating, Sugar Daddy, Sugar Mommy, Sugar Baby, 男找男, 女找女, 不限性別交友'
    },
    {
      property: 'og:title',
      content: 'SugarM｜不限性別的成人互助交友平台'
    },
    {
      property: 'og:description',
      content: '男生可以找男生，女生可以找女生；金主與 Baby 都能選擇男性、女性，或兩者皆可。'
    },
    {
      property: 'og:type',
      content: 'website'
    },
    {
      property: 'og:url',
      content: canonicalUrl
    },
    {
      property: 'og:image',
      content: 'https://sugarm.me/og-image.jpg'
    }
  ],
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})

// Lifecycle hooks
onMounted(async () => {
  try {
    // Show loading during initialization
    appStore.setLoading(true)
    
    // Initialize the app
    await authStore.initializeAuth()
    
    // Small delay to ensure all components are ready
    await new Promise(resolve => setTimeout(resolve, 100))
    
    // Set up PWA update detection
    // if ('serviceWorker' in navigator) {
    //   const registration = await navigator.serviceWorker.register('/sw.js')
    //   if (registration.waiting) {
    //     appStore.setUpdateAvailable(true)
    //   }
    // }
    
    // Handle online/offline status
    window.addEventListener('online', () => appStore.setOnlineStatus(true))
    window.addEventListener('offline', () => appStore.setOnlineStatus(false))
  } catch (error) {
    console.error('App initialization error:', error)
  } finally {
    // Hide loading screen
    appStore.setLoading(false)
  }
})

// Watch for route changes to track analytics
watch(() => route.path, (newPath) => {
  // Track page views (implement your analytics here)
  console.log('Page view:', newPath)
})

// Dark mode setup
const isDark = computed(() => appStore.isDarkMode)
watch(isDark, (dark) => {
  if (dark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}, { immediate: true })
</script>

<style scoped>
/* Page transition animations */
.page-enter-active,
.page-leave-active {
  transition: all 0.3s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateX(10px);
}

.page-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

/* Mobile-specific adjustments */
@media (max-width: 768px) {
  #app {
    /* Do not suppress the browser's horizontal history swipe on touch devices. */
    overscroll-behavior-y: contain;
    overscroll-behavior-x: auto;
  }
}

/* PWA safe area handling for iOS */
@supports (padding: max(0px)) {
  .safe-area-top {
    padding-top: max(env(safe-area-inset-top), 1rem);
  }
  
  .safe-area-bottom {
    padding-bottom: max(env(safe-area-inset-bottom), 1rem);
  }
}
</style>
